import { useEffect } from "react";

import { useAuth } from "providers/auth";
import { logError } from "utility/Function";
import { readLanguageChoice, resolveActiveLocale } from "utility/languagePreference";
import i18n, { I18N_ENABLED } from "lib/i18n";

/**
 * Keeps the rendered locale in step with everything known about the user's
 * preference — a choice made in this browser (which may predate having an
 * account, since the picker is offered on the sign-in screen) and, once a
 * profile loads, `learner.language`.
 *
 * Resolution lives in `resolveActiveLocale`; this is only the effect host
 * that feeds it. Renders nothing — mounted once near the tree root, below
 * AuthProvider.
 */
const LocaleSync: React.FC = () => {
  const { learner } = useAuth();
  // `|| null` (not `??`) so an empty-string preference falls through to the
  // browser/en-GB fallback rather than calling changeLanguage("").
  const preferred: string | null = learner?.language || null;
  const isExplicit: boolean = learner?.languageIsExplicit ?? false;

  useEffect(() => {
    // Build-gated off: init already pinned en-GB, so applying the learner's
    // preference here would be the one thing that leaks localisation past the
    // flag. Stay inert.
    if (!I18N_ENABLED) return;

    // Re-read on every learner change rather than once on mount: the menu
    // writes storage, and a sign-in can mark a pending choice as synced.
    const target = resolveActiveLocale({
      stored: readLanguageChoice(),
      profileLanguage: preferred,
    });

    if (i18n.language === target) return;

    i18n.changeLanguage(target).catch((error) => {
      logError("Failed to switch locale (non-fatal)", error as Error);
    });
  }, [preferred, isExplicit]);

  return null;
};

export default LocaleSync;
